import { call, takeEvery, takeLatest, select, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { hashClear } from 'containers/withRoute/actions';
import { API2_URL } from 'config/constants';
import request from 'utils/request';
import storage from 'utils/storage';
import { encrypt } from 'utils/cryptr';
import sleep from 'utils/sleep';
import firebaseEmail from 'utils/firebaseEmail';
import { addError } from 'containers/AlertService/actions';
import authMessages from 'components/Auth/messages';
import firebase from './firebase';
import {
  USER_SIGNOUT,
  USER_CHANGE,
  USER_SIMULATE,
  USER_RETRY_LOGIN,
  USER_LOADING,
  USER_API,
  USER_SEND_SIGNUP,
  USER_CONFIRM_SIGNUP,
} from './constants';
import { userLoading, userApi, userError, userChange } from './actions';
import makeSelectUser from './selectors';
import { sendSignUp, confirmSignUp } from './handleSignUp';
import loginWithFirebase from './loginWithFirebase';
import loginErrorKey, { LOGIN_FAILED } from './loginErrorKey';

// Individual exports for testing

function* waitLoading(action) {
  if (action.value === true) {
    yield sleep(5000);
    yield put(userLoading(false));
  }
}

function* doneLoading() {
  yield put(userLoading(false));
}

function* saveInfo() {
  const user = yield select(makeSelectUser());
  const { email, su, photoURL, api } = user;
  storage.setItem(
    'userInfo',
    encrypt(JSON.stringify({ email, su, photoURL, pmDate: api?.pm })),
  );
}

function* signOut() {
  try {
    storage.removeItem('userInfo');
    yield firebase.auth().signOut();
  } catch (error) {
    yield put(userError(error));
  }
}

export function* loginUser(firebaseUser) {
  try {
    const result = yield call(loginWithFirebase, firebaseUser);
    yield put(userApi(result));
    if (result.isLogged && window.location.hash === '#login') {
      yield put(hashClear());
    }
  } catch (error) {
    const key = loginErrorKey(error);
    yield put(userError(key));
    yield put(userLoading(false));
    yield put(addError({ title: authMessages[key] }));
    // Without an API session nothing signed-in works, so don't show the user
    // as signed in; only a temporary failure keeps the Firebase sign-in, to retry
    if (key !== LOGIN_FAILED) {
      yield call(signOut);
    }
  }
}

function* userChanged(action) {
  if (action.user) {
    if (!firebaseEmail(action.user)) {
      yield put(userError('noEmail'));
      return;
    }
    yield call(loginUser, action.user);
  }
}

function* retryLogin() {
  const firebaseUser = firebase.auth().currentUser;
  if (firebaseUser) {
    yield call(loginUser, firebaseUser);
  } else {
    // The Firebase sign-in is gone (expired, or signed out elsewhere): clear the
    // stale user, or the user control would wait for a session forever
    yield put(userChange(null));
  }
}

// Act as another user (super user only). The API checks the super user's
// own key, so the session of another user is never available without one.
export function* userSimulate(action) {
  const { userId } = action;
  const user = yield select(makeSelectUser());
  try {
    const result = yield request(`${API2_URL}/debug/users/${userId}/session`, {
      headers: { Authorization: user.api?.hashKey },
    });
    yield put(userApi(result));
  } catch (error) {
    yield put(userError(error));
  }
}

function* userSendSignUp(action) {
  const { type, email } = action;
  try {
    const result = yield sendSignUp(email);
    storage.setItem('email', email);
    yield put({ ...action, type: `${type}_SUCCESS`, payload: result });
  } catch (error) {
    yield put({ ...action, type: `${type}_ERROR`, error });
  }
}

function* userConfirmSignUp(action) {
  const { type, email } = action;
  try {
    const result = yield confirmSignUp(email);
    storage.removeItem('email');
    yield put(push(window.location.pathname));
    yield put({ ...action, type: `${type}_SUCCESS`, payload: result });
  } catch (error) {
    yield put({ ...action, type: `${type}_ERROR`, error });
  }
}

export default function* defaultSaga() {
  yield takeEvery(USER_SIGNOUT, signOut);
  yield takeEvery(USER_CHANGE, userChanged);
  yield takeEvery(USER_SIMULATE, userSimulate);
  yield takeLatest(USER_RETRY_LOGIN, retryLogin);
  yield takeEvery(USER_LOADING, waitLoading);
  yield takeEvery(USER_API, doneLoading);
  yield takeEvery(USER_API, saveInfo);
  yield takeLatest(USER_SEND_SIGNUP, userSendSignUp);
  yield takeLatest(USER_CONFIRM_SIGNUP, userConfirmSignUp);
}
